import { useState, useEffect, useRef } from 'react'
import { useAppStore } from '@/shared/store'
import { getErrorMessage } from '@/shared/lib'
import { defaultProfile } from '@/shared/constants'
import { signIn } from '@/api/apiClient'
import { Profile } from '@/shared/store/useAppStore'
import { useLoginAndProfileRepository } from '@/features/repositories/useLoginAndProfileRepository'
import {
    getFromLocalStorage,
    saveToLocalStorage,
} from '@/shared/localStorage/localStorageRepository'
import useKeyDown from '@/shared/hooks/useKeyDown'

const useUserInputHook = (
    handleLogInClick: (shouldOpenDeviceManager?: boolean) => void
) => {
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [inputErrors, setInputErrors] = useState({
        usernameError: '',
        passwordError: '',
        dataValidationError: '',
    })

    const usernameRef = useRef(username)
    const passwordRef = useRef(password)
    useEffect(() => {
        usernameRef.current = username
        passwordRef.current = password
    }, [username, password])

    const {
        setProfiles,
        setActualProfile,
        setEmail,
        setDevicesLimitExceeded,
        termsAndConditions,
    } = useAppStore()

    const areTermsAccepted = Boolean(termsAndConditions?.isTermsAccepted)
    const { fetchUserProfiles } = useLoginAndProfileRepository()
    const checkUsernameChange = (newUsername: string) => {
        setUsername(newUsername)

        setInputErrors((prev) => ({
            ...prev,
            dataValidationError: '',
        }))
        if (!newUsername)
            setInputErrors((prev) => ({
                ...prev,
                usernameError: 'usernameFieldCannotBeEmpty',
            }))
        else {
            setInputErrors((prev) => ({
                ...prev,
                usernameError: '',
            }))
        }
    }

    const checkPasswordChange = (newPassword: string) => {
        setPassword(newPassword)

        setInputErrors((prev) => ({
            ...prev,
            dataValidationError: '',
        }))

        if (!newPassword.length)
            setInputErrors((prev) => ({
                ...prev,
                passwordError: 'passwordFieldCannotBeEmpty',
            }))
        else
            setInputErrors((prev) => ({
                ...prev,
                passwordError: '',
            }))
    }

    const logIn = async () => {
        if (!areTermsAccepted) return

        const currentUsername = usernameRef.current
        const currentPassword = passwordRef.current
        setEmail(username)

        if (!currentUsername || !currentPassword) {
            setInputErrors((prev) => ({
                ...prev,
                usernameError: !currentUsername
                    ? 'usernameFieldCannotBeEmpty'
                    : '',
                passwordError: !currentPassword
                    ? 'passwordFieldCannotBeEmpty'
                    : '',
            }))
            return
        }

        try {
            const loginResponse = await signIn(currentUsername, currentPassword)

            if (!loginResponse?.token) {
                setInputErrors((prev) => ({
                    ...prev,
                    dataValidationError: 'loginDataIsInvalid',
                }))
                return
            }

            const hasExceededDevicesLimit = Boolean(
                loginResponse.devicesLimitExhausted
            )

            setDevicesLimitExceeded(hasExceededDevicesLimit)
            handleLogInClick(hasExceededDevicesLimit)
            setInputErrors({
                usernameError: '',
                passwordError: '',
                dataValidationError: '',
            })

            const profiles = (await fetchUserProfiles()) as Profile[]
            setProfiles(profiles?.length ? profiles : [defaultProfile])

            let selectedProfile = profiles.length ? profiles[0] : defaultProfile
            const storedProfileId = getFromLocalStorage('lastActualProfile')

            if (storedProfileId) {
                const profile = profiles.find(
                    (profile) => profile.id === storedProfileId
                )
                if (profile) {
                    selectedProfile = profile
                }
            }

            setActualProfile(selectedProfile)
            saveToLocalStorage('lastActualProfile', selectedProfile.id)
        } catch (error) {
            getErrorMessage('logIn', error)
        }
    }

    const isError = Boolean(
        inputErrors.usernameError || inputErrors.passwordError
    )

    useKeyDown(logIn)

    return {
        username,
        password,
        inputErrors,
        checkUsernameChange,
        checkPasswordChange,
        logIn,
        isError,
    }
}

export default useUserInputHook
